import { Lightbulb, MessageCircle, Send, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Separator } from './ui/separator';

const RAGChat = () => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    const messagesEndRef = useRef(null);

    // 滾動到最新消息
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // 載入初始數據
    useEffect(() => {
        loadSuggestions();
    }, []);

    const loadSuggestions = async () => {
        try {
            const response = await fetch('/api/rag/suggestions');
            const data = await response.json();
            if (data.success) {
                setSuggestions(data.suggestions);
            }
        } catch (error) {
            console.error('載入建議失敗:', error);
        }
    };

    const sendMessage = async (message = inputMessage) => {
        if (!message.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: message,
            timestamp: new Date().toLocaleTimeString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            // 使用法律助理的專用端點
            const response = await fetch('/api/ai/legal/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    question: message
                })
            });

            const data = await response.json();

            const assistantMessage = {
                id: Date.now() + 1,
                type: 'assistant',
                content: data.status === 'success' ? data.answer : '抱歉，我無法回答您的問題。',
                timestamp: new Date().toLocaleTimeString(),
                sources: data.sources || [],
                confidence: data.confidence || 0,
                contextUsed: data.sources ? data.sources.length : 0,
                totalDocsFound: data.sources ? data.sources.length : 0
            };

            setMessages(prev => [...prev, assistantMessage]);

        } catch (error) {
            console.error('發送消息失敗:', error);
            const errorMessage = {
                id: Date.now() + 1,
                type: 'assistant',
                content: '抱歉，系統發生錯誤，請稍後再試。',
                timestamp: new Date().toLocaleTimeString(),
                error: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        setMessages([]);
    };

    const useSuggestion = (suggestion) => {
        sendMessage(suggestion);
    };

    const getConfidenceColor = (confidence) => {
        if (confidence >= 0.8) return 'bg-green-500';
        if (confidence >= 0.6) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getConfidenceText = (confidence) => {
        if (confidence >= 0.8) return '高';
        if (confidence >= 0.6) return '中';
        return '低';
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-900">
            <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MessageCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                            智能法律助理
                        </h1>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={clearChat}
                        className="text-gray-600 dark:text-gray-300"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        清除對話
                    </Button>
                </div>
            </div>

            <div className="flex-1 flex flex-col p-4 space-y-4">
                {/* 聊天區域 */}
                <div className="flex-1 overflow-y-auto space-y-4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    {messages.length === 0 ? (
                        <div className="space-y-6">
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>歡迎使用智能法律助理!</p>
                                <p className="text-sm mt-2">您可以詢問專利、商標、著作權相關問題</p>
                            </div>

                            {/* 常見問題建議 */}
                            <div className="max-w-2xl mx-auto">
                                <div className="flex items-center justify-center gap-2 mb-4">
                                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        常見問題建議
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 gap-3">
                                    {suggestions.map((suggestion, index) => (
                                        <Card
                                            key={index}
                                            className="cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                            onClick={() => useSuggestion(suggestion)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-900 dark:text-gray-100">{suggestion}</span>
                                                    <Send className="w-4 h-4 text-gray-400" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[70%] p-3 rounded-lg ${message.type === 'user'
                                    ? 'bg-blue-600 text-white'
                                    : message.error
                                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-600'
                                    }`}>
                                    <div className="whitespace-pre-wrap">{message.content}</div>

                                    {/* 助理回答的附加信息 */}
                                    {message.type === 'assistant' && !message.error && (
                                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                                <span>置信度:</span>
                                                <div className={`w-2 h-2 rounded-full ${getConfidenceColor(message.confidence)}`}></div>
                                                <span>{getConfidenceText(message.confidence)} ({Math.round(message.confidence * 100)}%)</span>
                                                <Separator orientation="vertical" className="h-3" />
                                                <span>來源: {message.contextUsed || 0} 個文件</span>
                                            </div>

                                            {/* 來源信息 */}
                                            {message.sources && message.sources.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">參考來源:</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {message.sources.slice(0, 3).map((source, index) => (
                                                            <Badge key={index} variant="outline" className="text-xs">
                                                                {typeof source === 'string' ? source : (source.collection || source.source || `來源${index + 1}`)}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        {message.timestamp}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                                <div className="flex items-center space-x-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    <span className="text-gray-600 dark:text-gray-300">正在思考...</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* 輸入區域 */}
                <div className="flex gap-2">
                    <Input
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="請輸入您的問題..."
                        disabled={isLoading}
                        className="flex-1"
                    />
                    <Button
                        onClick={() => sendMessage()}
                        disabled={isLoading || !inputMessage.trim()}
                        size="icon"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RAGChat;
